import { ReactElement, useContext, useState } from "react";
import { IDictionaryNumericKey, IEAFProduction, IProduction, IStrandProduction } from "../../../types";
import { IRequest } from "../../../system/useFetch";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UpdateIcon from "@mui/icons-material/Update";
import { getPalette } from "../../../configuration/configuration";
import { formatDate, formatDateTimeWithSeconds, toTons } from "../../../system/utils";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WarningIcon from "@mui/icons-material/Warning";
import Detail from "../../../components/dialogs/detail";
import ProductionInformation from "../components/productionInformation";
import QueryDialog from "../../../components/dialogs/queryDialog";
import HeatInformation from "../../production/components/heatInformation";
import { IAuxiliaryData } from "../types";
import { ReportContext } from "./productionReportContext";
import CallSplitIcon from "@mui/icons-material/CallSplit";

interface IProps{
  row: IProduction;
  auxiliaryData: IDictionaryNumericKey<IAuxiliaryData>;
}

interface IDetailDialog<T> {
  open: boolean;
  item: T | null;
}


const ProductionTableRow = ({ row, auxiliaryData }: IProps): ReactElement => {
  const reportContext = useContext(ReportContext);
  const [open, setOpen] = useState(false);
  const [openDetailRM, setOpenDetailRM] = useState<IDetailDialog<IStrandProduction>>({ open: false, item: null });
  const [openDetailEAF, setOpenDetailEAF] = useState<IDetailDialog<IRequest>>({ open: false, item: null });

  const data = row.productionMachines[0] as IStrandProduction;

  const onHeatClick = (productionMachineID: number, heatNumber: string) => {
    setOpenDetailEAF({
      open: true,
      item: {
        module: "meltShop",
        query: "getInformation",
        payload: [
          {
            heatNumber,
            module: "production",
            subModule: "meltShop",
            productionMachineIDs: productionMachineID,
            zone: "EAF",
            flag: "eaf"
          }
        ] }
    });
  };

  const selectedProduction = () => {
    reportContext.setSelected({ productionID: row.productionID, productionMachineID: null });
  };

  if (!auxiliaryData[row.productionID]) {
    return <TableRow style={{ width: "100%" }} />;
  }

  const direction =  data.totalBillets > data.accumulateBillets ? "DOWN" : data.totalBillets < data.accumulateBillets ? "UP" : "";

  const backgroundInd = auxiliaryData[row.productionID].std === "GOOD"
    ? getPalette().statusColor.good.background
    : auxiliaryData[row.productionID].std === "WARNING"
      ? getPalette().statusColor.warning.background
      : getPalette().statusColor.error.background;

  const selected = reportContext.selected;


  let endDatetime = row.endDatetime ?? 0;
  row.productionMachines.map(machine => endDatetime = Math.max(endDatetime, machine.endDatetime ?? 0));

  const warning = !auxiliaryData[row.productionID] || auxiliaryData[row.productionID].weight === 0;

  const cellStyle = {
    color: warning ? "#303030" : undefined
  };

  return (
    <>
      <TableRow style={{ width: "100%",  background: warning ? "#fff59d" : undefined }} hover={true}
        onClick={selectedProduction}
        selected={selected.productionID === row.productionID}>
        <TableCell className="table-cell" style={cellStyle}>
          <div className="display-flex-row">
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} style={cellStyle}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>

            <UpdateIcon style={{
              color: "whitesmoke",
              background: backgroundInd,
              marginLeft: "0.5em", marginTop: "0.1em" }} />
          </div>
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>
          {formatDate(data.shiftHistory.factoryDay)}
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>
          {data.shiftHistory.name}
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>
          {data.crew.name}
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>
          <div className="display-flex-row flex-nowrap">
            {data.millHeat}
            {data.partitions > 1 &&
            <Tooltip title={"Partitioned by shift"} >
              <CallSplitIcon style={{ color: warning ? "#303030" : "whitesmoke", background: getPalette().statusColor.warning.background, verticalAlign: "center", marginLeft: "0.5em" }} fontSize="small"/>
            </Tooltip>
            }
          </div>
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>
          <div className="display-flex-row flex-nowrap align-items-center">
            {data.heatData && data.eafProductionMachineID &&
            <IconButton aria-label="edit" size={"small"} onClick={() => onHeatClick(data.eafProductionMachineID ?? 0, data.heatNumber)}>
              <Tooltip title="Show Detail">
                <OpenInNewIcon style={{ color: warning ? "#303030" : undefined }}/>
              </Tooltip>
            </IconButton>
            }
            {data.heatNumber}
          </div>
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>
          {data.steelCode}
        </TableCell>
        <TableCell align={"right"} className="table-cell" style={cellStyle}>
          <div>
            {data.totalBillets}
          </div>
        </TableCell>
        <TableCell align={"right"} className="table-cell" style={cellStyle}>
          <div>
            {auxiliaryData[row.productionID].billets}
          </div>
        </TableCell>
        <TableCell align={"right"} className="table-cell" style={cellStyle}>
          <div className="display-flex-row flex-nowrap">
            <div className="flex-grow-1">{data.accumulateBillets}</div>
            { direction === "UP" && <ArrowUpwardIcon style={{ color: "whitesmoke", background: getPalette().statusColor.good.background, verticalAlign: "center", marginLeft: "0.5em" }} fontSize="small"/> }
            { direction === "DOWN" && <ArrowDownwardIcon style={{ color: "whitesmoke", background: getPalette().statusColor.error.background, verticalAlign: "center", marginLeft: "0.5em" }} fontSize="small"/> }
            { direction === "" && <div style={{ minWidth: 21, marginLeft: "0.5em" }} /> }
          </div>
        </TableCell>
        <TableCell align={"right"} className="table-cell" style={cellStyle}>
          <div>
            {data.accumulateCobbleBillets}
          </div>
        </TableCell>
        <TableCell align={"right"} className="table-cell" style={cellStyle}>
          <div>
            {data.accumulateCutUpBillets}
          </div>
        </TableCell>
        <TableCell align={"right"} className="table-cell" style={cellStyle}>
          <div className="display-flex-row">
            <div className="flex-grow-1">{toTons(auxiliaryData[row.productionID].weight).toFixed(2)}</div>
            { auxiliaryData[row.productionID].pendingWeight && <WarningIcon style={{ color: getPalette().statusColor.warning.background, verticalAlign: "center", marginLeft: "0.5em" }} fontSize="small"/> }
            { !auxiliaryData[row.productionID].pendingWeight && <div style={{ minWidth: 21, marginLeft: "0.5em" }} /> }
          </div>
        </TableCell>
        <TableCell className="table-cell" style={cellStyle}>{formatDateTimeWithSeconds(row.beginDatetime)}</TableCell>
        <TableCell className="table-cell" style={cellStyle}>{endDatetime > 0 ? formatDateTimeWithSeconds(endDatetime) : ""}</TableCell>
      </TableRow>
      <TableRow >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: "none" }} colSpan={data.partitions === 1 ? 9 : 11}  className="table-cell">
          <Collapse in={open} timeout="auto" unmountOnExit={true}>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom={true} component="div">
                Detail
              </Typography>
              <Table size="small" stickyHeader={true} aria-label="machines">
                <TableHead>
                  <TableRow>
                    <TableCell className="table-header"/>
                    <TableCell className="table-header">Machine</TableCell>
                    {data.partitions > 1 &&
                      <>
                        <TableCell className="table-header">Factory Day</TableCell>
                        <TableCell className="table-header">Shift</TableCell>
                        <TableCell className="table-header">Crew</TableCell>
                      </>
                    }
                    <TableCell className="table-header" colSpan={2}>Begin</TableCell>
                    <TableCell className="table-header" colSpan={2}>End</TableCell>
                    <TableCell className="table-header" align="right">Total Billets</TableCell>
                    <TableCell className="table-header" align="right">Total Weight [US ton]</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.productionMachines.map((productionMachine, index) => {

                    const machineData = productionMachine as IStrandProduction;
                    const aux = auxiliaryData[row.productionID].machineData.find(x => x.Id === machineData.productionMachineID);

                    const selectedProductionMachine = () => {
                      reportContext.setSelected({ productionID: null, productionMachineID: machineData.productionMachineID });
                    };

                    return(
                      <TableRow key={`detail-${machineData.productionMachineID}-${index}`} hover={true}
                        selected={selected.productionMachineID === machineData.productionMachineID}
                        onClick={selectedProductionMachine}>
                        <TableCell className="table-cell">
                          <IconButton aria-label="edit" size={"small"} onClick={() => setOpenDetailRM({ open: true, item: machineData })}>
                            <Tooltip title="Show Detail">
                              <OpenInNewIcon/>
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                        <TableCell className="table-cell">{machineData.machine.name}</TableCell>
                        {data.partitions > 1 &&
                          <>
                            <TableCell className="table-cell">
                              {formatDate(machineData.shiftHistory.factoryDay)}
                            </TableCell>
                            <TableCell className="table-cell">
                              {machineData.shiftHistory.name}
                            </TableCell>
                            <TableCell className="table-cell">
                              {machineData.crew.name}
                            </TableCell>
                          </>
                        }
                        <TableCell className="table-cell" colSpan={2}>{formatDateTimeWithSeconds(machineData.beginDatetime)}</TableCell>
                        <TableCell className="table-cell" colSpan={2}>{formatDateTimeWithSeconds(machineData.endDatetime)}</TableCell>
                        <TableCell align={"right"}>{aux ? aux.billets : "---"}</TableCell>
                        <TableCell align={"right"}>
                          <div className="display-flex-row">
                            <div className="flex-grow-1">{aux ? toTons(aux.weight).toFixed(2) : "---"}</div>
                            { aux && aux.pendingWeight && <WarningIcon style={{ color: getPalette().statusColor.warning.background, verticalAlign: "center", marginLeft: "0.5em" }} fontSize="small"/> }
                            { aux && !aux.pendingWeight && <div style={{ minWidth: 21, marginLeft: "0.5em" }} /> }
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {openDetailRM.open && openDetailRM.item &&
      <Detail closed={() => setOpenDetailRM({ open: false, item: null })} fullWidth={true} maxWidth={"xl"} title="Detail">
        <ProductionInformation productionItem={ openDetailRM.item} height={500}/>
      </Detail>
      }
      {openDetailEAF.open && openDetailEAF.item &&
      <QueryDialog request={openDetailEAF.item}
        onDataArrived={(detail) => <HeatInformation productionItem={detail as IEAFProduction} height={500}/>}
        onClosed={() => setOpenDetailEAF({ open: false, item: null })}/>
      }
    </>
  );

};

export default ProductionTableRow;
